import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfig } from '@app/app-config.model';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

const defaultConfiguration: AppConfig = {
  env: {
    name: 'dev',
    version: '2.0.0',
    api: 'http://localhost:8080/api',
    websocket: {
      notifications: 'ws://localhost:8080/wsnotifications'
    }
  },
  assets: {
    external: true,
    blob: 'https://vincletestblob.blob.core.windows.net/marketing/vfs'
  },
  language: {
    default: 'en'
  },
  theme: {
    name: 'vfs-theme'
  }
};

@Injectable()
export class AppConfigService {
  private configuration: AppConfig;

  constructor(private http: HttpClient) {}

  load(translateService: TranslateService) {
    return new Promise<boolean>((resolve: (value: boolean) => void): void => {
      const url = `assets/configuration/configuration.json`;
      this.http
        .get(url)
        .pipe(
          map((configuration: AppConfig) => {
            this.configuration = configuration;
            let lang = configuration.language.default || 'en';
            translateService.setDefaultLang(lang);
            translateService.use(lang).subscribe(() => {
              resolve(true);
            });
          }),
          catchError((err: { status: number }, caught: Observable<void>) => {
            console.warn(`Configuration ${this.configuration.env.name} not found. Loading default configuration.`);
            this.configuration = defaultConfiguration;
            resolve(false);
            return of({});
          })
        )
        .subscribe();
    });
  }

  /**
   * Get configuration object
   */
  get(): AppConfig {
    return this.configuration;
  }

  /**
   * Returns environment api
   */
  getApi(): string {
    return this.configuration.env.api;
  }

  /**
   * Returns the connection string of the Notifications' WebSocket.
   */
  getNotificationsWebSocket(): string {
    return this.configuration.env.websocket.notifications;
  }

  /**
   * Returns environment name
   */
  getEnvName(): string {
    return this.configuration.env.name;
  }

  /**
   * Returns environment version
   */
  getVersion(): string {
    return this.configuration.env.version;
  }

  /**
   * Returns if external assets is active
   */
  isExternal(): boolean {
    return this.configuration.assets.external;
  }

  /**
   * Returns blob url
   */
  getBlob(): string {
    return `${this.configuration.assets.blob}`;
  }

  /**
   * Returns the ADFS active state
   */
  isAdfsActive(): boolean {
    return this.configuration.env.adfs || false;
  }

  /**
   * Returns the help for importing data
   */
  getHelpImportData(): string {
    if (this.configuration.help) {
      return this.configuration.help.importData || '';
    } else {
      return '';
    }
  }

  /**
   * @returns The uuid needed to recover user password. This uuid is configured in BE and
   * must be identical in FE, in order to be translated into an URL
   */
  getPwdUuid(): string {
    return this.configuration.recovery?.pwdContext;
  }

  /**
   * @returns The uuid needed to recover user name. This uuid is configured in BE and
   * must be identical in FE, in order to be translated into an URL
   */
  getUsrUuid(): string {
    return this.configuration.recovery?.usrContext;
  }
}
